import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYMENT_LINK = 'https://rzp.io/rzp/BMzp0qQx';

const RegistrationConfirm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const enquiryData = location.state?.enquiry;
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Redirect if no enquiry data
    if (!enquiryData) {
      navigate('/');
      return;
    }

    // Fetch pricing info
    fetchPricing();
  }, [enquiryData, navigate]);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/enquiry/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = () => {
    // Redirect to payment link
    window.location.href = PAYMENT_LINK;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-xl font-semibold text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-yellow-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Registration Successful!
          </h1>
          <p className="text-lg text-gray-600">
            Thank you for registering with Alee Club
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Registration Details */}
          <Card className="shadow-xl border-2 border-gray-100">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Your Details
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Name:</span>
                  <span className="text-gray-900 font-semibold">{enquiryData?.name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Age:</span>
                  <span className="text-gray-900 font-semibold">{enquiryData?.age} years</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Phone:</span>
                  <span className="text-gray-900 font-semibold">{enquiryData?.phone}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Email:</span>
                  <span className="text-gray-900 font-semibold text-sm">{enquiryData?.email}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600 font-medium">City:</span>
                  <span className="text-gray-900 font-semibold">{enquiryData?.city}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Details */}
          <Card className="shadow-xl border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-white">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Registration Fee
              </h2>

              {pricing?.is_early_bird ? (
                <div className="space-y-4">
                  <div className="text-center py-4 bg-yellow-100 rounded-lg border-2 border-yellow-400">
                    <Badge className="bg-yellow-500 text-white mb-2 text-sm px-4 py-1">
                      🎉 EARLY BIRD SPECIAL
                    </Badge>
                    <div className="text-5xl font-bold text-yellow-700 mb-2">
                      ₹{pricing.current_price}
                    </div>
                    <div className="text-sm text-gray-600 line-through mb-1">
                      Regular Price: ₹{pricing.regular_price}
                    </div>
                    <div className="text-lg font-semibold text-green-600">
                      You Save: ₹{pricing.regular_price - pricing.current_price}!
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <div className="text-sm font-medium text-red-800 mb-1">
                      ⏰ Offer Ends In
                    </div>
                    <div className="text-3xl font-bold text-red-600">
                      {pricing.days_remaining} {pricing.days_remaining === 1 ? 'Day' : 'Days'}
                    </div>
                    <div className="text-xs text-red-600 mt-1">
                      Don't miss this limited-time offer!
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="text-5xl font-bold text-gray-900 mb-2">
                    ₹{pricing.current_price}
                  </div>
                  <div className="text-sm text-gray-600">
                    Standard Registration Fee
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment Button */}
        <div className="text-center">
          <Card className="shadow-2xl border-2 border-red-200 bg-white">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Complete Your Registration
              </h3>
              <p className="text-gray-600 mb-6">
                Click below to proceed with the payment and secure your spot!
              </p>
              <Button
                onClick={handlePayment}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-4 px-12 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                <svg className="w-6 h-6 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Proceed to Payment - ₹{pricing?.current_price}
              </Button>
              <p className="text-xs text-gray-500 mt-4">
                🔒 Secure payment powered by Razorpay
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Need help? Contact us at{' '}
            <span className="font-semibold text-red-600">Alee Club Support (8 AM – 10 PM)</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationConfirm;